import type { Destination, FlowMessage, HoursCheck, InboundRoute, IvrMenu, Queue, RingGroup } from '@arcnet/contracts';
import {
  useHoursChecks, useInboundRoutes, useIvrs, useMessages, useQueues, useRingGroups,
} from '../../lib/api/hooks-tms.js';

/** Every routing piece of an account, as the pages already load them. */
export interface RoutingData {
  routes: InboundRoute[]; ivrs: IvrMenu[]; groups: RingGroup[]; queues: Queue[];
  messages: FlowMessage[]; hours: HoursCheck[];
}

export function useRoutingData(tenantId: string): RoutingData {
  const routes = useInboundRoutes(tenantId);
  const ivrs = useIvrs(tenantId);
  const groups = useRingGroups(tenantId);
  const queues = useQueues(tenantId);
  const messages = useMessages(tenantId);
  const hours = useHoursChecks(tenantId);
  return {
    routes: routes.data?.data ?? [], ivrs: ivrs.data?.data ?? [], groups: groups.data?.data ?? [],
    queues: queues.data?.data ?? [], messages: messages.data?.data ?? [], hours: hours.data?.data ?? [],
  };
}

/**
 * Who sends calls to a piece: "Main line", "IVR menu · Main menu (key 9)". A piece is shared by
 * whatever points at it, so a change to it is a change for all of them (docs/41 §2), and this is
 * how a screen says so before the change is made rather than after.
 */
export function referrers(type: string, id: string, d: RoutingData): string[] {
  const hit = (x: Destination | null | undefined): boolean => !!x && x.type === type && 'id' in x && x.id === id;
  const out: string[] = [];
  for (const r of d.routes) if (hit(r.destination) || hit(r.closed_destination)) out.push(r.name);
  for (const m of d.ivrs) {
    for (const o of m.options) if (hit(o.destination)) out.push(`IVR menu · ${m.name} (key ${o.digit})`);
    if (hit(m.timeout_destination) || hit(m.invalid_destination)) out.push(`IVR menu · ${m.name}`);
  }
  for (const g of d.groups) if (hit(g.timeout_destination)) out.push(`Hunt list · ${g.name}`);
  for (const q of d.queues) if (hit(q.timeout_destination) || hit(q.exit_destination)) out.push(`Queue · ${q.name}`);
  for (const m of d.messages) if (hit(m.next_destination)) out.push(`Message · ${m.name}`);
  for (const h of d.hours) if (hit(h.open_destination) || hit(h.closed_destination)) out.push(`Hours check · ${h.name}`);
  return [...new Set(out)];
}

/** What follows a shared schedule: the routes and hours checks that open and close on it. */
export function scheduleUsers(id: string, d: RoutingData): string[] {
  const on = (h: { mode: string; schedule_id?: string | null }) => h.mode === 'schedule' && h.schedule_id === id;
  return [
    ...d.routes.filter((r) => on(r.hours)).map((r) => r.name),
    ...d.hours.filter((h) => on(h.hours as { mode: string; schedule_id?: string | null })).map((h) => `Hours check · ${h.name}`),
  ];
}

/**
 * The bin's label for a piece something still sends calls to: the server refuses that delete
 * (assertNotReferenced), so the bin is off and says why (UX review, 21 October 2026).
 */
export function inUseLabel(name: string, users: string[]): string {
  return `${name} is used by ${users.join(', ')}. Change ${users.length === 1 ? 'that' : 'those'} first.`;
}
